import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
    Card,
    Col,
    Container,
    Row,
    CardBody,
    Label,
    Form,
    Input,
    Button,
} from "reactstrap";
import { addClinic, getClinic, getRegion, getWardByRegion } from "service/api";

const defaultValue = {
    name: "",
    code: "",
    email: "",
    contactno: "",
    logo: "",
    region: "",
    ward: "",
    faculty_manager: "",
    services_rendered: "",
    address: "",
    comment: "",
};

const surfaceStyle = {
    border: "1px solid #e8edf3",
    borderRadius: "20px",
    boxShadow: "0 18px 40px rgba(16, 24, 40, 0.08)",
    overflow: "hidden",
};

const sectionTitleStyle = {
    fontSize: 15,
    fontWeight: 700,
    color: "#143b64",
    letterSpacing: "0.02em",
    marginBottom: 16,
};

const labelStyle = {
    fontSize: 12,
    fontWeight: 700,
    color: "#47607a",
    letterSpacing: "0.03em",
    textTransform: "uppercase",
    marginBottom: 8,
};

const inputStyle = {
    minHeight: 46,
    borderRadius: 12,
    borderColor: "#d6e1ec",
    boxShadow: "none",
};

const textareaStyle = {
    minHeight: 110,
    borderRadius: 14,
    borderColor: "#d6e1ec",
    boxShadow: "none",
    resize: "vertical",
};

const AddGuestClinic = () => {
    const [clinic, setClinic] = useState(defaultValue);
    const [ward, setWard] = useState([]);
    const [region, setRegion] = useState([]);
    const [clinics, setClinics] = useState([]);
    const [file, setFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [emailError, setEmailError] = useState("");
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    const onValueChange = async (e) => {
        const { name, value, files } = e.target;

        if (name === "email") {
            const emailExists = clinics.some((clinicItem) => clinicItem.email === value);
            setEmailError(emailExists ? "This email is already in use." : "");
        }

        if (name === "logo") {
            const selectedFile = files?.[0] || null;
            if (filePreview) {
                URL.revokeObjectURL(filePreview);
            }
            setFile(selectedFile);
            setFilePreview(selectedFile ? URL.createObjectURL(selectedFile) : null);
            return;
        }

        setClinic((prev) => ({ ...prev, [name]: value }));

        if (name === "region") {
            try {
                const response = await getWardByRegion(value);
                setWard(response.data || []);
            } catch (error) {
                console.error("Error fetching wards:", error);
                setWard([]);
            }
        }
    };

    useEffect(() => {
        const loadData = async () => {
            try {
                const [regionResponse, clinicResponse] = await Promise.all([
                    getRegion(),
                    getClinic(),
                ]);
                setRegion(regionResponse.data || []);
                setClinics(clinicResponse.data || []);
            } catch (error) {
                console.error("Error loading guest clinic form data:", error);
            }
        };

        loadData();

        return () => {
            if (filePreview) {
                URL.revokeObjectURL(filePreview);
            }
        };
    }, [filePreview]);

    const handleAddGuestClinic = async (e) => {
        e.preventDefault();

        if (emailError) {
            toast.error(emailError, {
                position: "top-center",
                autoClose: 2000,
            });
            return;
        }

        setSaving(true);
        try {
            const formData = new FormData();
            Object.keys(clinic).forEach((key) => {
                formData.append(key, clinic[key]);
            });

            if (file) {
                formData.append("logo", file);
            }

            formData.append("guestpractice", "1");
            formData.append("is_active", "0");

            await addClinic(formData);
            toast.success("Guest practice saved successfully.", {
                position: "top-center",
                autoClose: 1500,
            });
            navigate("/clinic");
        } catch (error) {
            console.error("Error saving guest practice:", error);
            toast.error("Failed to save guest practice");
        } finally {
            setSaving(false);
        }
    };

    document.title = "Add Guest Practice | Form";

    return (
        <React.Fragment>
            <div className="page-content" style={{ paddingTop: 12 }}>
                <Container fluid style={{ paddingLeft: 12, paddingRight: 12 }}>
                    <Card
                        style={{
                            ...surfaceStyle,
                            background:
                                "linear-gradient(135deg, #103b66 0%, #1a5c86 46%, #d4ede7 100%)",
                            marginBottom: 24,
                            marginTop: 0,
                        }}
                    >
                        <CardBody style={{ padding: "28px 30px" }}>
                            <Row className="align-items-center g-4">
                                <Col lg={8}>
                                    <div style={{ color: "#fff" }}>
                                        <div
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 8,
                                                padding: "7px 12px",
                                                borderRadius: 999,
                                                background: "rgba(255,255,255,0.14)",
                                                fontSize: 12,
                                                fontWeight: 700,
                                                letterSpacing: "0.06em",
                                                textTransform: "uppercase",
                                                marginBottom: 14,
                                            }}
                                        >
                                            New Practice Onboarding
                                        </div>
                                        <h2
                                            style={{
                                                fontSize: "clamp(1.6rem, 2.5vw, 2.4rem)",
                                                fontWeight: 800,
                                                lineHeight: 1.1,
                                                marginBottom: 10,
                                            }}
                                        >
                                            Create a practice profile
                                        </h2>
                                        <p
                                            style={{
                                                margin: 0,
                                                maxWidth: 720,
                                                color: "rgba(255,255,255,0.86)",
                                                fontSize: 15,
                                                lineHeight: 1.65,
                                            }}
                                        >
                                            Add the core practice details here. Guest practices are saved as inactive by default,
                                            It will be activated by the admin within a few days, and you will be notified via email.
                                        </p>
                                    </div>
                                </Col>
                                <Col lg={4}>
                                    <div
                                        style={{
                                            background: "rgba(255,255,255,0.14)",
                                            border: "1px solid rgba(255,255,255,0.18)",
                                            borderRadius: 20,
                                            padding: 20,
                                            color: "#fff",
                                            backdropFilter: "blur(8px)",
                                        }}
                                    >
                                        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", opacity: 0.85 }}>
                                            Quick Notes
                                        </div>
                                        <div style={{ marginTop: 14, fontSize: 14, lineHeight: 1.7 }}>
                                            <div>Practice starts as inactive</div>
                                            <div>Email is checked against existing clinics</div>
                                            <div>Logo preview appears instantly after upload</div>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    <Form onSubmit={handleAddGuestClinic}>
                        <Row className="g-4">
                            <Col xl={8}>
                                <Card style={surfaceStyle}>
                                    <CardBody style={{ padding: 28 }}>
                                        <div style={sectionTitleStyle}>Practice Identity</div>
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Label style={labelStyle}>Practice Name</Label>
                                                <Input
                                                    type="text"
                                                    name="name"
                                                    value={clinic.name}
                                                    placeholder="Enter practice name"
                                                    onChange={onValueChange}
                                                    style={inputStyle}
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Label style={labelStyle}>Practice Code</Label>
                                                <Input
                                                    type="text"
                                                    name="code"
                                                    value={clinic.code}
                                                    placeholder="Enter practice code"
                                                    onChange={onValueChange}
                                                    style={inputStyle}
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Label style={labelStyle}>Practice Email</Label>
                                                <Input
                                                    type="email"
                                                    name="email"
                                                    value={clinic.email}
                                                    placeholder="name@practice.com"
                                                    onChange={onValueChange}
                                                    invalid={!!emailError}
                                                    style={inputStyle}
                                                />
                                                {emailError && <div className="text-danger mt-1">{emailError}</div>}
                                            </Col>
                                            <Col md={6}>
                                                <Label style={labelStyle}>Practice Contact No.</Label>
                                                <Input
                                                    type="text"
                                                    name="contactno"
                                                    value={clinic.contactno}
                                                    maxLength={11}
                                                    placeholder="E.g - 0123456789"
                                                    onChange={onValueChange}
                                                    style={inputStyle}
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Label style={labelStyle}>Faculty Manager</Label>
                                                <Input
                                                    type="text"
                                                    name="faculty_manager"
                                                    value={clinic.faculty_manager}
                                                    placeholder="Faculty manager"
                                                    onChange={onValueChange}
                                                    style={inputStyle}
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Label style={labelStyle}>Services Rendered</Label>
                                                <Input
                                                    type="text"
                                                    name="services_rendered"
                                                    value={clinic.services_rendered}
                                                    placeholder="Services rendered"
                                                    onChange={onValueChange}
                                                    style={inputStyle}
                                                />
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>

                                <Card style={{ ...surfaceStyle, marginTop: 24 }}>
                                    <CardBody style={{ padding: 28 }}>
                                        <div style={sectionTitleStyle}>Location and Notes</div>
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Label style={labelStyle}>Region</Label>
                                                <Input
                                                    type="select"
                                                    name="region"
                                                    value={clinic.region}
                                                    onChange={onValueChange}
                                                    style={inputStyle}
                                                >
                                                    <option value="">Select region</option>
                                                    {region.map((s) => (
                                                        <option key={s._id} value={s._id}>
                                                            {s.region}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </Col>
                                            <Col md={6}>
                                                <Label style={labelStyle}>Provience</Label>
                                                <Input
                                                    type="select"
                                                    name="ward"
                                                    value={clinic.ward}
                                                    onChange={onValueChange}
                                                    style={inputStyle}
                                                >
                                                    <option value="">Select ward</option>
                                                    {ward.map((s) => (
                                                        <option key={s._id} value={s._ward}>
                                                            {s.ward}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </Col>
                                            <Col md={6}>
                                                <Label style={labelStyle}>Address</Label>
                                                <Input
                                                    type="textarea"
                                                    name="address"
                                                    value={clinic.address}
                                                    placeholder="Enter practice address"
                                                    onChange={onValueChange}
                                                    style={textareaStyle}
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Label style={labelStyle}>Comments</Label>
                                                <Input
                                                    type="textarea"
                                                    name="comment"
                                                    value={clinic.comment}
                                                    placeholder="Any extra notes for review"
                                                    onChange={onValueChange}
                                                    style={textareaStyle}
                                                />
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>

                            <Col xl={4}>
                                <Card style={surfaceStyle}>
                                    <CardBody style={{ padding: 28 }}>
                                        <div style={sectionTitleStyle}>Branding</div>
                                        <div
                                            style={{
                                                border: "1.5px dashed #b8cfe3",
                                                borderRadius: 20,
                                                background: "linear-gradient(180deg, #f8fbff 0%, #eef6fb 100%)",
                                                padding: 20,
                                            }}
                                        >
                                            <Label style={labelStyle}>Practice Logo</Label>
                                            <Input
                                                type="file"
                                                name="logo"
                                                onChange={onValueChange}
                                                style={{ ...inputStyle, paddingTop: 10, paddingBottom: 10 }}
                                            />
                                            <div style={{ marginTop: 14, fontSize: 13, color: "#587088", lineHeight: 1.6 }}>
                                                Upload a clean logo to make the guest practice easier to identify during review.
                                            </div>
                                            <div
                                                style={{
                                                    marginTop: 18,
                                                    minHeight: 220,
                                                    borderRadius: 18,
                                                    background: "#fff",
                                                    border: "1px solid #dde8f2",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    padding: 16,
                                                }}
                                            >
                                                {filePreview ? (
                                                    <img
                                                        src={filePreview}
                                                        alt="Practice Logo Preview"
                                                        style={{ maxWidth: "100%", maxHeight: 180, objectFit: "contain", borderRadius: 12 }}
                                                    />
                                                ) : (
                                                    <div style={{ textAlign: "center", color: "#7b90a5" }}>
                                                        <div style={{ fontSize: 34, lineHeight: 1, marginBottom: 10 }}>
                                                            <i className="mdi mdi-image-outline" />
                                                        </div>
                                                        <div style={{ fontWeight: 700, color: "#35516d", marginBottom: 4 }}>Logo preview</div>
                                                        <div style={{ fontSize: 13 }}>Your uploaded image will appear here</div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div
                                            style={{
                                                marginTop: 20,
                                                padding: 18,
                                                borderRadius: 18,
                                                background: "#fff8e8",
                                                border: "1px solid #f1ddb0",
                                                color: "#6b5310",
                                                fontSize: 13,
                                                lineHeight: 1.7,
                                            }}
                                        >
                                            This guest practice will be saved as <strong>Inactive</strong> first. It will be activated by the admin within a few days, and you will be notified via email.
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>

                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-4">
                            <Link to="/login" className="btn btn-light" style={{ borderRadius: 12, padding: "11px 18px", border: "1px solid #d6e1ec" }}>
                                Back to Login
                            </Link>
                            <div className="d-flex gap-2">
                                {/* <Link to="/Addclinic" className="btn btn-outline-primary" style={{ borderRadius: 12, padding: "11px 18px" }}>
                                    Standard Practice Form
                                </Link> */}
                                <Button
                                    color="primary"
                                    type="submit"
                                    disabled={saving}
                                    style={{
                                        borderRadius: 12,
                                        padding: "11px 22px",
                                        background: "linear-gradient(135deg, #103b66 0%, #1d6c8f 100%)",
                                        border: "none",
                                        boxShadow: "0 12px 28px rgba(16, 59, 102, 0.24)",
                                    }}
                                >
                                    {saving ? "Saving..." : "Save Guest Practice"}
                                </Button>
                            </div>
                        </div>
                    </Form>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default AddGuestClinic;
